// orgContext — pre-login tenant resolution via subdomain.
//
// At boot (before the user logs in) we detect the subdomain and, if one is
// present, call GET /v1/orgs/by-subdomain/:slug on Global-Admin (the platform
// control plane). That resolver reads the AUTHORITATIVE country_us.tenant
// registry — not the per-tenant common.tenants mirror — so we get the tenant's
// identity AND its CRM branding (the lettermark logo) before any user logs in.
// The result drives the login-page heading ("Sign in to {displayName}") and the
// CRM shell brand mark.
//
// Authoritative tenant for API calls: useAuth().user.tenantId (from the
// signed token), never this context.  See authStore.ts.
//
// Degradation: if the fetch fails or the backend returns 404, org stays null
// and the app falls back to the existing behaviour (generic heading +
// env.tenantId from authStore).

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

import { env } from '../config/env';
import { applyDocumentBranding } from './documentBranding';
import { getCurrentSubdomain } from './subdomain';

// --- Types -------------------------------------------------------------------

/**
 * Per-tenant CRM branding authored on `country_us.tenant.branding`. A lettermark
 * logo config the shell renders (initials + accent letter + name). All fields
 * optional so a partial/absent row degrades to the shell default. Non-secret.
 */
export interface OrgBranding {
  /** Discriminator; only `'lettermark'` today. */
  kind?: string;
  /** e.g. "RET". */
  initials?: string;
  /** 0-based index of the initial rendered in the accent colour (the "E"). */
  accentIndex?: number;
  /** Full name, e.g. "Rheora Energy Texas". */
  name?: string;
  /** Sub-line, e.g. "Texas · ERCOT". */
  tagline?: string;
  /** Accent hex, e.g. "#a8392b". */
  accentColor?: string;
}

export interface ResolvedOrg {
  /** Tenant UUID returned by the backend — informational only pre-login. */
  tenantId: string;
  /** The URL slug that triggered the resolution. */
  slug: string;
  /** Human-readable display name shown on the login page. */
  displayName: string;
  /** CRM branding (lettermark logo config), or null when the tenant has none. */
  branding: OrgBranding | null;
}

interface OrgContextValue {
  /** Resolved org, or null if no subdomain / resolution failed. */
  currentOrg: ResolvedOrg | null;
  /** True while the initial fetch is in-flight. */
  isResolvingOrg: boolean;
}

// --- Context -----------------------------------------------------------------

const OrgContext = createContext<OrgContextValue>({
  currentOrg: null,
  isResolvingOrg: false,
});

// --- Fetch helper ------------------------------------------------------------

/** Global-Admin returns the `{ data, meta }` envelope. */
interface OrgBySubdomainResponse {
  data?: {
    tenantId: string;
    slug: string;
    displayName: string;
    branding?: OrgBranding | null;
  };
}

/** Accept only a plain object as branding; anything else degrades to null. */
function coerceBranding(raw: unknown): OrgBranding | null {
  return raw !== null && typeof raw === 'object' && !Array.isArray(raw)
    ? (raw as OrgBranding)
    : null;
}

async function fetchOrgBySlug(slug: string): Promise<ResolvedOrg | null> {
  try {
    const url = `${env.adminApiUrl}/v1/orgs/by-subdomain/${encodeURIComponent(slug)}`;
    const response = await fetch(url);
    if (!response.ok) return null; // 404 or any non-2xx
    const body = (await response.json()) as OrgBySubdomainResponse;
    const data = body.data;
    if (data === undefined) return null;
    return {
      tenantId: data.tenantId,
      slug: data.slug,
      displayName: data.displayName,
      branding: coerceBranding(data.branding),
    };
  } catch {
    // Network error — degrade gracefully, do not block app render.
    return null;
  }
}

// --- Provider ----------------------------------------------------------------

/** Mount once at the application root (inside QueryClientProvider / providers). */
export function OrgProvider({ children }: { children: ReactNode }): JSX.Element {
  const [currentOrg, setCurrentOrg] = useState<ResolvedOrg | null>(null);
  const [isResolvingOrg, setIsResolvingOrg] = useState(false);

  useEffect(() => {
    // Prefer the subdomain slug; on a subdomain-less host (plain localhost, or a
    // deploy served from a bare domain) fall back to the env-configured slug so
    // branding still resolves. Empty env.orgSlug ⇒ subdomain-only behaviour.
    const slug = getCurrentSubdomain() ?? (env.orgSlug !== '' ? env.orgSlug : null);
    if (!slug) return; // no subdomain and no fallback — keep org = null

    setIsResolvingOrg(true);
    void fetchOrgBySlug(slug).then((org) => {
      setCurrentOrg(org);
      setIsResolvingOrg(false);
    });
  }, []); // runs once at mount

  // Reflect the resolved tenant in the browser tab (title + monogram favicon).
  // Re-runs when the org lands; a null org leaves index.html's static defaults.
  useEffect(() => {
    applyDocumentBranding(currentOrg);
  }, [currentOrg]);

  return (
    <OrgContext.Provider value={{ currentOrg, isResolvingOrg }}>
      {children}
    </OrgContext.Provider>
  );
}

// --- Hook --------------------------------------------------------------------

/**
 * Returns the pre-login resolved org (display branding) and the in-flight
 * flag. Safe to call from any component; returns null on plain localhost.
 */
export function useCurrentOrg(): OrgContextValue {
  return useContext(OrgContext);
}
